import { FC, useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from '@hooks/store';
import { getSuperSimplifierAccountsAsync, getSuperSimplifierAccountAsync } from '@store/reducers/supersimplifier/thunks';
import { ScreenState } from '@src/app/enums';
import { SupersimplifierStore, selectSupersimplifier, setSuperSimplifierAccountParameter } from '@store/reducers/supersimplifier';
import Container from '@mui/material/Container';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import SupersimplifierTable, { SupersimplifierTableRow, TableHeaders } from '@components/superTables/supersimplifierTable';
import { homeUrl } from '@utils/UrlBuilder';
import { Alert, Box, Breadcrumbs, FormControl, InputLabel, Typography } from '@mui/material';
import { SuperSimplifierAccountSummary } from 'clientportalshared';
import { Home } from '@mui/icons-material';
import { useHistory } from 'react-router';
import { PageContent } from '@components/pageContent';

const Pension: FC = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const supersimplifierStore: SupersimplifierStore = useAppSelector(selectSupersimplifier);

  const [selectedAccount, setSelectedAccount] = useState('');

  const getSuperSimplifierAccount = async (accountList: SuperSimplifierAccountSummary[] | undefined) => {
    if (accountList?.length) {
      const pensionAcc = accountList.find((acc) => acc.type === 'Pension');
      if (pensionAcc && pensionAcc.accountNumber) {
        await dispatch(
          getSuperSimplifierAccountAsync({
            accountNumber: pensionAcc.accountNumber,
          })
        );
      }
    }
  };

  const getPageData = () => {
    dispatch(getSuperSimplifierAccountsAsync())
      .unwrap()
      .then((responce) => {
        if (responce?.data?.length) {
          const pensionAccount = responce.data.find((acc) => acc.type === 'Pension');
          if (pensionAccount?.accountNumber && pensionAccount.type) {
            setSelectedAccount(pensionAccount.accountNumber);
          }
        }
        getSuperSimplifierAccount(responce.data);
      });
  };

  useEffect(() => {
    getPageData();
  }, []);

  const tableHeaders: TableHeaders = { header1: 'Description', header2: 'Value' };
  let pensionTable: SupersimplifierTableRow[] = [];
  let pensionBankTable: SupersimplifierTableRow[] = [];

  if (supersimplifierStore.superSimplifierAccount?.pensionDetails) {
    const {
      bankAccountBsb,
      bankAccountNumber,
      bankAccountName,
      financialYearPaymentAmountTillDate,
      minimumAnnualPaymentAmount,
      maximumAnnualPaymentAmount,
      nextPaymentDate,
      paymentFrequency,
      paymentOption,
      paymentAmount,
      pensionType,
    } = supersimplifierStore.superSimplifierAccount?.pensionDetails;

    let paymentFrequencyDsp = '';
    switch (paymentFrequency) {
      case 'H':
        paymentFrequencyDsp = 'Half-Yearly';
        break;
      case 'I':
        paymentFrequencyDsp = 'Monthly';
        break;
      case 'L':
        paymentFrequencyDsp = 'Monthly';
        break;
      case 'Q':
        paymentFrequencyDsp = 'Quarterly';
        break;
      case 'Y':
        paymentFrequencyDsp = 'Yearly';
        break;
      case 'F':
        paymentFrequencyDsp = 'Fortnightly';
        break;
      default:
        paymentFrequencyDsp = '';
    }

    const minimumAnnualPaymentAmountDsp: number | string = minimumAnnualPaymentAmount || '';
    const maximumAnnualPaymentAmountDsp: number | string = maximumAnnualPaymentAmount || '';

    pensionTable = [
      { label: 'Pension Type', value: pensionType },
      {
        label: 'Payment Option',
        value: paymentOption,
      },
      {
        label: 'Payment Amount',
        value: paymentAmount,
      },
      { label: 'Next Payment Date', value: nextPaymentDate },
      { label: 'Payment Frequency', value: paymentFrequencyDsp },
      { label: 'Total Paid Financial Year to Date', value: financialYearPaymentAmountTillDate },
      { label: 'Minimum Annual Payment Amount', value: minimumAnnualPaymentAmountDsp },
      { label: 'Maximum Annual Payment Amount *', value: maximumAnnualPaymentAmountDsp },
    ];

    pensionBankTable = [
      { label: 'BSB', value: bankAccountBsb },
      {
        label: 'Account Number',
        value: bankAccountNumber,
      },
      {
        label: 'Account Name',
        value: bankAccountName,
      },
    ];
  }

  const handleAccountChange = (event: SelectChangeEvent) => {
    setSelectedAccount(event.target.value);
    dispatch(setSuperSimplifierAccountParameter(event.target.value));
    dispatch(
      getSuperSimplifierAccountAsync({
        accountNumber: event.target.value,
      })
    );
  };

  let errorMessage = null;
  if (supersimplifierStore.superSimplifierAccountState === ScreenState.ErrorUnableToLoadData) {
    errorMessage =
      'There is currently no Pension information for this account. If the account has been recently established it may take a few days for this data to be populated. Please contact your adviser our or client service team for assistance.';
    pensionTable = [];
    pensionBankTable = [];
  } else if (supersimplifierStore.superSimplifierAccountState === ScreenState.Loading) {
    pensionTable = [];
    pensionBankTable = [];
  }

  return (
    <Container maxWidth={false}>
      <Box display="flex" flexDirection="column" gap="24px" mb={6}>
        <Box display="flex" alignItems="center" gap="16px" mt={2}>
          <Breadcrumbs>
            <Typography
              onClick={() => history.push(homeUrl())}
              sx={{
                display: 'flex',
                alignItems: 'center',
                ':hover': {
                  cursor: 'pointer',
                },
              }}
              color="inherit"
            >
              <Home sx={{ mr: 0.5 }} fontSize="small" />
              Dashboard
            </Typography>
            <Typography fontWeight={600} fontSize="20px" color="#222222">
              Pension Details
            </Typography>
          </Breadcrumbs>

          <Box flex={1} />
          <FormControl>
            <InputLabel id="selected-account-select-label">Account</InputLabel>
            <Select
              labelId="selected-account-select-label"
              id="selected-account-select"
              value={selectedAccount}
              defaultValue={selectedAccount}
              label="Account"
              onChange={handleAccountChange}
            >
              {supersimplifierStore?.superSimplifierAccounts?.data?.map((i) => (
                <MenuItem key={i.accountNumber} value={i.accountNumber ?? ''}>{`${i.accountNumber ?? ''} - ${i.name ?? ''}`}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {errorMessage && (
          <Alert variant="outlined" severity="info">
            {errorMessage}
          </Alert>
        )}

        {!errorMessage && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
            }}
          >
            <PageContent>
              <SupersimplifierTable tableHeaders={tableHeaders} data={pensionTable} header="Pension Details" />
            </PageContent>
            <Typography sx={{ paddingLeft: '20px' }}>
              <i>* Maximum payment limits only apply to Transition to Retirement (TTR) accounts</i>
            </Typography>
            <PageContent>
              <SupersimplifierTable tableHeaders={tableHeaders} data={pensionBankTable} header="Pension Bank Account" />
            </PageContent>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default Pension;
